import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Sparkles, Box, Zap, Trophy } from "lucide-react";
import { api, img } from "../lib/api";
import { productPath } from "../lib/seo";
import ProductCard from "../components/ProductCard";
import { CATS } from "../lib/categories";
import { t } from "../lib/i18n";
import RecentlyViewed from "../components/RecentlyViewed";
import BannerCarousel from "../components/BannerCarousel";
import FestivalDeals from "../components/FestivalDeals";


function Countdown() {
  const [s, setS] = useState(() => 86400 - (Math.floor(Date.now() / 1000) % 86400));
  useEffect(() => { const t = setInterval(() => setS((v) => (v > 0 ? v - 1 : 86400)), 1000); return () => clearInterval(t); }, []);
  const f = (n) => String(n).padStart(2, "0");
  return <span data-testid="deals-countdown" className="font-mono bg-black/40 px-3 py-1 rounded-lg text-[#ffb703]">{f(Math.floor(s / 3600))}:{f(Math.floor((s % 3600) / 60))}:{f(s % 60)}</span>;
}

function Hero({ feature }) {
  return (
    <section className="hero-radial">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-12 pb-16 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 space-y-6 rise">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#00f0ff] bg-[#00f0ff]/10 px-3 py-1.5 rounded-full"><Sparkles size={14} />AI clarity photos · 3D product view</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]">Shop bold.<br /><span className="text-[#ff5500]">See every detail</span> before it arrives.</h1>
          <p className="text-slate-400 text-sm sm:text-base max-w-lg">Mobiles, electronics, fashion, beauty, home, grocery and more — with 360° views, honest reviews and checkout in seconds via Razorpay or Cash on Delivery.</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/products" data-testid="hero-shop-now" className="btn-glow inline-flex items-center gap-2 bg-[#ff5500] hover:bg-[#ff6b1a] px-6 py-3 rounded-full font-semibold transition-colors">Shop now <ArrowRight size={18} /></Link>
            {feature && <Link to={productPath(feature)} data-testid="hero-explore-3d" className="inline-flex items-center gap-2 border border-white/20 hover:border-white px-6 py-3 rounded-full font-semibold transition-colors"><Box size={18} />Explore in 3D</Link>}
          </div>
        </div>
        {feature && (
          <Link to={productPath(feature)} className="lg:col-span-5 relative block">
            <div className="absolute -inset-6 bg-gradient-to-tr from-[#ff5500]/30 to-[#7b2cbf]/30 blur-3xl rounded-full" />
            <div className="floaty relative rounded-3xl overflow-hidden border border-white/10 aspect-[4/5] max-w-sm mx-auto">
              <img src={img(feature.images[0])} alt={feature.title} className="w-full h-full object-cover" />
              <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-black/90 to-transparent">
                <p className="text-xs text-[#ffb703] uppercase tracking-wider">Deal of the day</p>
                <p className="font-display font-bold text-lg">{feature.title}</p>
              </div>
            </div>
          </Link>
        )}
      </div>
    </section>
  );
}

const Ticker = () => {
  const items = [[Truck, "FREE standard delivery on every order"], [ShieldCheck, "Secure Razorpay checkout"], [Zap, "Cash on Delivery available"], [Sparkles, "AI clarity product photos"], [Box, "360° 3D product view"]];
  const row = [...items, ...items];
  return (
    <div className="border-y border-white/10 bg-[#ff5500] text-black overflow-hidden">
      <div className="marquee flex w-max gap-12 py-3 font-display font-bold uppercase text-sm">
        {row.map(([I, t], i) => <span key={i} className="flex items-center gap-2 whitespace-nowrap"><I size={16} />{t}</span>)}
      </div>
    </div>
  );
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const [best, setBest] = useState([]);
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/products?sort=rating&limit=100").then((r) => setProducts(r.data));
    api.get("/products?sort=bestseller&limit=12").then((r) => setBest(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, []);
  const counts = Object.fromEntries(cats.map((c) => [c.slug, c.count]));
  const rail = (slug) => products.filter((p) => p.category === slug).slice(0, 8);
  const deals = [...products].sort((a, b) => (b.mrp - b.price) / b.mrp - (a.mrp - a.price) / a.mrp).slice(0, 8);

  return (
    <div data-testid="home-page">
      <FestivalDeals />
      <BannerCarousel fallback={<Hero feature={products.find((p) => p.category === "electronics") || products[0]} />} />
      <Ticker />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14">
        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-8">{t("Shop by category")}</h2>
        <div className="grid grid-cols-5 lg:grid-cols-10 gap-3 sm:gap-5">
          {CATS.map((c, i) => (
            <Link key={c.slug} to={`/category/${c.slug}`} data-testid={`category-card-${c.slug}`} style={{ animationDelay: `${i * 40}ms` }} className="rise group flex flex-col items-center gap-2 text-center">
              <div className="w-full aspect-square rounded-2xl overflow-hidden border border-white/10 group-hover:border-[#ff5500]/60 transition-colors">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              </div>
              <p className="text-[11px] sm:text-sm font-semibold leading-tight">{t(c.name)}</p>
              <p className="text-[10px] text-slate-500 hidden sm:block">{counts[c.slug] || 0} items</p>
            </Link>
          ))}
        </div>
      </section>
      <RecentlyViewed />
      {best.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-14" data-testid="bestsellers-section">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2"><Trophy className="text-[#ffb703]" />Bestsellers</h2>
            <span className="text-xs text-slate-400 hidden sm:inline">Most bought on EAZYSHOPY</span>
            <Link to="/products?sort=bestseller&title=Bestsellers" className="ml-auto text-sm text-[#ff5500] hover:underline" data-testid="bestsellers-view-all">View all</Link>
          </div>
          <div className="flex gap-5 overflow-x-auto no-scrollbar pt-3 pl-3 pb-2">
            {best.map((p, i) => (
              <div key={p.id} className="w-60 shrink-0 relative" data-testid={`bestseller-${i + 1}`}>
                <span className="absolute -top-2 -left-2 z-10 w-8 h-8 rounded-full bg-[#ffb703] text-black font-black text-sm grid place-items-center shadow-lg">#{i + 1}</span>
                <ProductCard p={p} i={i} />
              </div>
            ))}
          </div>
        </section>
      )}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="rounded-3xl bg-gradient-to-br from-[#1a1d28] to-[#12141c] border border-white/10 p-6 sm:p-8">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2"><Zap className="text-[#ffb703]" />Flash deals</h2>
            <Countdown />
            <Link to="/products?sort=price_asc" className="ml-auto text-sm text-[#ff5500] hover:underline" data-testid="deals-view-all">View all</Link>
          </div>
          <div className="flex gap-5 overflow-x-auto no-scrollbar pb-2">
            {deals.map((p, i) => <div key={p.id} className="w-60 shrink-0"><ProductCard p={p} i={i} /></div>)}
          </div>
        </div>
      </section>
      {[["grocery", "Grocery essentials", "Fresh veggies, fruits, spices & honey"], ["mobiles", "Best of mobiles", "Latest 5G phones at lowest prices"]].map(([slug, title, sub]) => (
        <section key={slug} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-16" data-testid={`rail-${slug}`}>
          <div className="flex items-end gap-4 mb-6">
            <div><h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</h2><p className="text-sm text-slate-500">{sub}</p></div>
            <Link to={`/category/${slug}`} className="ml-auto text-sm text-[#ff5500] hover:underline">View all</Link>
          </div>
          <div className="flex gap-5 overflow-x-auto no-scrollbar pb-2">
            {rail(slug).map((p, i) => <div key={p.id} className="w-56 shrink-0"><ProductCard p={p} i={i} /></div>)}
          </div>
        </section>
      ))}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-8">Trending now</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {products.slice(0, 8).map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
        </div>
      </section>
    </div>
  );
}
